import Swal from "sweetalert2";
import type { AxiosError, AxiosRequestConfig, Method } from "axios";
import axios from "axios";
import { firebaseAuth, firebaseStorage } from "plugins/firebase";
import type { UploadTaskSnapshot} from "firebase/storage";
import { ref, uploadBytesResumable, deleteObject } from "firebase/storage";

type ApiErrorBody = { error?: string; message?: string };

function toRejectionError(data: ApiErrorBody | undefined): Error & ApiErrorBody {
  const err = new Error(data?.error ?? data?.message ?? 'Error en la petición');
  return Object.assign(err, data);
}

const urlBase = import.meta.env.VITE_API_MOBILE_URL || "/api/mobile";

const getHeaders = async (baseHeaders?: Record<string, string>): Promise<Record<string, string>> => {
  const DEFAULT_HEADERS: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  const headers = { ...(baseHeaders ?? DEFAULT_HEADERS) };
  await firebaseAuth.authStateReady();
  const currentUser = firebaseAuth.currentUser;
  if (currentUser) {
    try {
      const idToken = await currentUser.getIdToken();
      headers["Authorization"] = `Bearer ${idToken}`;
    } catch {
      // Proceed without auth header if token refresh fails
    }
  }
  return headers;
};

const _http = async <T>(
  method: Method,
  url: string,
  data?: unknown,
  headers?: Record<string, string>
): Promise<T> => {
  const newHeaders = await getHeaders(headers);
  return new Promise<T>((resolve, reject) => {
    url = urlBase + url;
    const config: AxiosRequestConfig = {
      method,
      url,
      data,
      headers: newHeaders,
    };

    axios<T>(config)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error: AxiosError<ApiErrorBody>) => {
        if (error.response?.data?.error) {
          void Swal.fire({
            title: "¡Error!",
            text: error.response.data.error,
            icon: "error",
            confirmButtonText: "Aceptar",
          });
        }
        reject(toRejectionError(error.response?.data));
      });
  });
};

async function _download(method: Method, routePath: string, data?: unknown, filename?: string): Promise<unknown> {
  try {
      const url = `${urlBase}${routePath}`;
      const response = await fetch(url, {
          method: method.toUpperCase(),
          headers: await getHeaders(),
          body: data ? JSON.stringify(data) : undefined,
      });
      const resObj = {
          filename: filename || fnGetFileNameFromContentDispostionHeader(String(response.headers.get('content-disposition'))),
          blob: await response.blob(),
      };
      const newBlob = new Blob([resObj.blob], {
          type: `${response.headers.get('content-type')}`,
      });

      // MS Edge and IE don't allow using a blob object directly as link href, instead it is necessary to use msSaveOrOpenBlob
      const legacyNavigator = window.navigator as Navigator & { msSaveOrOpenBlob?: (blob: Blob) => void };
      if (legacyNavigator.msSaveOrOpenBlob) {
          legacyNavigator.msSaveOrOpenBlob(newBlob);
          return true;
      }

      // For other browsers: create a link pointing to the ObjectURL containing the blob.
      const objUrl = window.URL.createObjectURL(newBlob);

      const link = document.createElement('a');
      link.href = objUrl;
      link.download = resObj.filename;
      link.click();

      // For Firefox it is necessary to delay revoking the ObjectURL.
      setTimeout(() => {
          window.URL.revokeObjectURL(objUrl);
      }, 250);

      return true;
  } catch {
      return false;
  }
}

interface UploadResult {
  error?: string;
  file?: string;
  name?: string;
  msg?: string;
  message?: string;
}

async function _uploadFileToGoogleCloudStorage(file: File, fileName: string, onProgress?: (progress: number, uploaded: boolean) => void): Promise<UploadResult> {
  const FILE = file;
  const FILENAME = fileName;
  return new Promise<UploadResult>((resolve) => {
      try {
          // Crear referencia usando la nueva API de Firebase v9+
          const storageRef = ref(firebaseStorage, FILENAME);
          const uploadTask = uploadBytesResumable(storageRef, FILE);

          uploadTask.on(
              'state_changed',
              (snapshot: UploadTaskSnapshot) => {
                  const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                  if (onProgress) onProgress(progress, false);
              },
              (error: Error) => {
                  console.error('[firebase] uploadTask ERROR', error);
                  return resolve({ error: error.toString() });
              },
              () => {
                  _http<UploadResult>('post', '/storage/register', { fileName: FILENAME })
                    .then((registerResult) => {
                      if (onProgress) onProgress(100, true);
                      return resolve(registerResult);
                    })
                    .catch((error: ApiErrorBody) => {
                      return resolve({ error: error.error ?? 'Error al subir el archivo' });
                    });
              },
          );
      } catch (error) {
          console.error('[firebase] Error al iniciar upload:', error);
          return resolve({ error: error instanceof Error ? error.toString() : 'Error desconocido' });
      }
  });
}

async function _removeFileFromGoogleCloudStorage(fileName: string): Promise<UploadResult> {
  try {
    // Eliminar del servidor primero
    const serverResult = await _http<UploadResult>('delete', '/storage/upload', { fileName });

    // Luego eliminar de Firebase Storage usando la nueva API
    const storageRef = ref(firebaseStorage, fileName);
    await deleteObject(storageRef);

    return serverResult;
  } catch (error) {
    console.error('[firebase] Error al eliminar archivo:', error);
    // Intentar eliminar solo del servidor si falla Firebase
    return _http<UploadResult>('delete', '/storage/upload', { fileName });
  }
}

function fnGetFileNameFromContentDispostionHeader(header: string) {
  const contentDispostion = header ? header.split(';') : [];
  const fileNameToken = `filename=`;
  let filename = 'sample.txt';
  for (const thisValue of contentDispostion) {
      if (thisValue.trim().indexOf(fileNameToken) === 0) {
          filename = decodeURIComponent(thisValue.trim().replace(fileNameToken, ''));
          break;
      }
  }
  return filename;
}

const http = {
  get<T>(url: string, headers?: Record<string, string>): Promise<T> {
    return _http<T>("get", url, undefined, headers);
  },

  post<T, B = Record<string, unknown>>(url: string, data: B, headers?: Record<string, string>): Promise<T> {
    return _http<T>("post", url, data, headers);
  },

  put<T, B = Record<string, unknown>>(url: string, data: B, headers?: Record<string, string>): Promise<T> {
    return _http<T>("put", url, data, headers);
  },

  patch<T, B = Record<string, unknown>>(url: string, data: B): Promise<T> {
    return _http<T>("patch", url, data);
  },

  delete<T = void>(url: string, data?: unknown): Promise<T> {
    return _http<T>("delete", url, data);
  },

  download(url: string, filename: string) {
    return _download('get', url, null, filename);
  },
  uploadFile(file: File, fileName: string, onProgress?: (progress: number, uploaded: boolean) => void) {
    return _uploadFileToGoogleCloudStorage(file, fileName, onProgress);
  },
  removeFile(fileName: string) {
    return _removeFileFromGoogleCloudStorage(fileName);
  }
};

export default http;
